import { Avatar } from '@seek/avatar';
import { useTranslations } from '@vocab/react';
import {
  Box,
  Button,
  Heading,
  IconShare,
  IconTick,
  Inline,
  Stack,
  Text,
} from 'braid-design-system';
import { useCallback, useRef, useState } from 'react';

import type { ProfileBase } from '../../../types/profile.types';
import { LastInteraction } from '../../Profile/LastInteraction/LastInteraction';
import { Location } from '../../Profile/Location/Location';
import { Nationality } from '../../Profile/Nationality/Nationality';
import { Salary } from '../../Profile/Salary/Salary';
import { VerifiedCredentialBadge } from '../../Profile/VerifiedCredentialBadge/VerifiedCredentialBadge';
import { WorkHistory } from '../../Profile/WorkHistory/WorkHistory';
import { VerifiedIdTickTooltip } from '../../VerifiedIdTickTooltip/VerifiedIdTickTooltip';
import { LastUpdated } from '../LastUpdated/LastUpdated';

import translations from './.vocab';

export const ProfileViewCard = ({
  profile,
  isSignedIn,
  profileUrl,
  renderActions,
  onLastInteractionClick,
}: {
  profile: ProfileBase;
  isSignedIn: boolean;
  profileUrl: string; // This is required (even if sometimes passed as empty string) to ensure that it isn't forgotten when used elsewhere.
  renderActions?: (profile: ProfileBase) => React.ReactNode;
  onLastInteractionClick?: () => void;
}) => {
  const {
    displayName,
    digitalIdentity,
    hasVerifiedCredentials,
    workHistories,
    homeLocation,
    salary,
    nationalities,
    hasEthicalHiring,
    lastInteractionDuration,
    lastModifiedDurationLabel,
  } = profile;

  const { t } = useTranslations(translations);

  const hasWorkHistories = workHistories && workHistories.length > 0;
  const [isCopied, setIsCopied] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();

  const handleCopyLink = useCallback(() => {
    const fullUrl = `${window.location.origin}${profileUrl}`;

    navigator.clipboard.writeText(fullUrl);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setIsCopied(true);

    // Reset after 2.8 seconds
    timeoutRef.current = setTimeout(() => {
      setIsCopied(false);
    }, 2800);
  }, [profileUrl]);

  return (
    <Stack space="small">
      <Box marginBottom="xxsmall" position="relative">
        <Box position="absolute" style={{ top: '-88px' }}>
          <Avatar border name={displayName} size="xlarge" variant="initials" />
        </Box>
      </Box>
      <Inline space="medium" alignY="top">
        <Box style={{ marginRight: 'auto' }}>
          <Stack space="small">
            <Inline space="xxsmall" alignY="center">
              <Heading level="2">{displayName}</Heading>
              {digitalIdentity.isVerified ? (
                <Text>
                  {' '}
                  <VerifiedIdTickTooltip
                    hasEthicalHiring={Boolean(hasEthicalHiring)}
                    isSignedIn={isSignedIn}
                    verifiedName={digitalIdentity.verifiedName}
                    profileName={displayName}
                  />
                </Text>
              ) : null}
            </Inline>
            {hasVerifiedCredentials || digitalIdentity.isVerified ? (
              <Box aria-label="Verified ID">
                <VerifiedCredentialBadge />
              </Box>
            ) : null}
            <Box marginTop="xxsmall">
              <Stack space="small">
                {hasWorkHistories && (
                  <WorkHistory
                    workHistories={workHistories}
                    count={1}
                    hideDuration={true}
                    boldText={false}
                    showIcon
                  />
                )}

                <Location homeLocation={homeLocation} />
                {nationalities ? (
                  <Nationality nationalities={nationalities} />
                ) : null}
                {salary ? <Salary salary={salary} /> : null}
                {lastInteractionDuration ? (
                  <Box paddingTop="xsmall">
                    <LastInteraction
                      lastInteractionDuration={lastInteractionDuration}
                      onClick={() => onLastInteractionClick?.()}
                    />
                  </Box>
                ) : null}
                {renderActions ? (
                  <Box paddingTop="xsmall">{renderActions(profile)}</Box>
                ) : null}
              </Stack>
            </Box>
          </Stack>
        </Box>

        {lastModifiedDurationLabel ? (
          <LastUpdated duration={lastModifiedDurationLabel} />
        ) : null}

        {profileUrl ? (
          <Button
            size="small"
            tone="neutral"
            variant="soft"
            onClick={handleCopyLink}
            icon={isCopied ? <IconTick /> : <IconShare />}
          >
            {isCopied ? t('LINK_COPIED') : t('SHARE')}
          </Button>
        ) : null}
      </Inline>
    </Stack>
  );
};
